import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Users,
  UserCheck,
  UserX,
  Clock,
  Search,
  Plus,
  Pencil,
  Trash2,
  Upload,
  RotateCcw,
  LogOut,
  Loader2,
  Lock,
  Download,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from "@/components/ui/table";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div
      className="bg-white rounded-sm p-5"
      style={{ border: "1px solid rgba(197,165,114,0.3)" }}
    >
      <div className="flex items-center gap-3">
        <div
          className="w-10 h-10 rounded-sm flex items-center justify-center"
          style={{ backgroundColor: color + "20" }}
        >
          <Icon size={20} style={{ color }} />
        </div>
        <div>
          <p className="text-xs uppercase tracking-wider" style={{ color: "#666" }}>
            {label}
          </p>
          <p className="font-serif text-2xl font-semibold" style={{ color: "#2C2C2C" }}>
            {value}
          </p>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ confermato }) {
  if (confermato === true)
    return (
      <Badge
        data-testid="badge-confirmed"
        className="text-xs"
        style={{ backgroundColor: "#8B9A6B", color: "white" }}
      >
        Confermato
      </Badge>
    );
  if (confermato === false)
    return (
      <Badge
        data-testid="badge-declined"
        variant="secondary"
        className="text-xs"
        style={{ backgroundColor: "#E5E5E5", color: "#666" }}
      >
        Non partecipa
      </Badge>
    );
  return (
    <Badge
      data-testid="badge-pending"
      variant="outline"
      className="text-xs"
      style={{ borderColor: "#C5A572", color: "#C5A572" }}
    >
      In attesa
    </Badge>
  );
}

export default function AdminPage() {
  const [token, setToken] = useState(() => sessionStorage.getItem("adminToken"));
  const [password, setPassword] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);

  const [dashboard, setDashboard] = useState(null);
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("tutti");

  const [editGuest, setEditGuest] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showCSVDialog, setShowCSVDialog] = useState(false);
  const [deleteGuest, setDeleteGuest] = useState(null);

  const [formData, setFormData] = useState({
    nome: "",
    cognome: "",
    gruppo: "",
    max_accompagnatori: 1,
  });
  const [saving, setSaving] = useState(false);

  const downloadExcel = () => {
    window.open(`${API}/admin/guests/export-excel`, "_blank");
  };

  const login = async () => {
    setLoginLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      sessionStorage.setItem("adminToken", res.data.token);
      setToken(res.data.token);
      toast.success("Accesso effettuato");
    } catch {
      toast.error("Password non corretta");
    } finally {
      setLoginLoading(false);
    }
  };

  const logout = () => {
    sessionStorage.removeItem("adminToken");
    setToken(null);
    toast.success("Disconnesso");
  };

  const loadDashboard = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/dashboard`);
      setDashboard(res.data);
    } catch (err) {
      console.error("Errore caricamento dashboard:", err);
    }
  }, []);

  const loadGuests = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== "tutti") params.stato = statusFilter;
      if (searchQ) params.search = searchQ;
      const res = await axios.get(`${API}/admin/guests`, { params });
      setGuests(res.data);
    } catch (err) {
      console.error("Errore caricamento invitati:", err);
      toast.error("Errore nel caricamento invitati");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, searchQ]);

  useEffect(() => {
    if (token) {
      loadDashboard();
      loadGuests();
    }
  }, [token, loadDashboard, loadGuests]);

  const handleSave = async () => {
    if (!formData.nome || !formData.cognome) {
      toast.error("Nome e cognome sono obbligatori");
      return;
    }
    setSaving(true);
    try {
      if (editGuest) {
        await axios.put(`${API}/admin/guests/${editGuest.id}`, formData);
        toast.success("Invitato aggiornato");
      } else {
        await axios.post(`${API}/admin/guests`, formData);
        toast.success("Invitato aggiunto");
      }
      setShowAddDialog(false);
      setEditGuest(null);
      setFormData({ nome: "", cognome: "", gruppo: "", max_accompagnatori: 1 });
      loadGuests();
      loadDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteGuest) return;
    try {
      await axios.delete(`${API}/admin/guests/${deleteGuest.id}`);
      toast.success("Invitato eliminato");
      setDeleteGuest(null);
      loadGuests();
      loadDashboard();
    } catch {
      toast.error("Errore nell'eliminazione");
    }
  };

  const handleResetRSVP = async (guestId) => {
    try {
      await axios.post(`${API}/admin/guests/${guestId}/reset-rsvp`);
      toast.success("RSVP resettato");
      loadGuests();
      loadDashboard();
    } catch {
      toast.error("Errore nel reset");
    }
  };

  const handleCSVUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await axios.post(`${API}/admin/guests/import-csv`, formData);
      toast.success(`Importati ${res.data.imported} invitati`);
      if (res.data.errors?.length > 0) {
        toast.warning(`${res.data.errors.length} errori durante l'importazione`);
      }
      setShowCSVDialog(false);
      loadGuests();
      loadDashboard();
    } catch {
      toast.error("Errore nell'importazione");
    }
    e.target.value = "";
  };

  const downloadTemplate = () => {
    const csv = "nome,cognome,gruppo,max_accompagnatori\nMario,Rossi,Famiglia Rossi,2\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "template_invitati.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const openEdit = (g) => {
    setEditGuest(g);
    setFormData({
      nome: g.nome,
      cognome: g.cognome,
      gruppo: g.gruppo || "",
      max_accompagnatori: g.max_accompagnatori,
    });
    setShowAddDialog(true);
  };

  const openAdd = () => {
    setEditGuest(null);
    setFormData({ nome: "", cognome: "", gruppo: "", max_accompagnatori: 1 });
    setShowAddDialog(true);
  };

  // Group guests by gruppo
  const groupedGuests = guests.reduce((acc, g) => {
    const group = g.gruppo || "Senza gruppo";
    if (!acc[group]) acc[group] = [];
    acc[group].push(g);
    return acc;
  }, {});

  // Login screen
  if (!token) {
    return (
      <main
        data-testid="admin-login-page"
        className="min-h-screen flex items-center justify-center bg-warm-white px-6"
      >
        <div
          className="w-full max-w-sm bg-white rounded-sm p-8"
          style={{ border: "1px solid rgba(197,165,114,0.3)" }}
        >
          <div className="text-center mb-6">
            <Lock size={32} className="mx-auto mb-3 text-gold" />
            <h1 className="font-serif text-2xl" style={{ color: "#2C2C2C" }}>
              Area Admin
            </h1>
            <p className="text-sm mt-1" style={{ color: "#666" }}>
              Inserisci la password per accedere
            </p>
          </div>
          <Input
            data-testid="admin-password-input"
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && login()}
            className="h-12 text-base rounded-sm mb-4"
            style={{ border: "1px solid rgba(197,165,114,0.4)" }}
          />
          <Button
            data-testid="admin-login-button"
            className="w-full btn-press touch-target text-base rounded-sm"
            style={{ backgroundColor: "#8B9A6B", color: "white", minHeight: "48px" }}
            onClick={login}
            disabled={loginLoading}
          >
            {loginLoading ? <Loader2 className="animate-spin mr-2" size={18} /> : null}
            Accedi
          </Button>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="admin-page" className="min-h-screen bg-warm-white">
      {/* Admin Header */}
      <header
        className="px-6 py-4 flex items-center justify-between"
        style={{
          backgroundColor: "rgba(255,253,248,0.97)",
          borderBottom: "1px solid rgba(197,165,114,0.2)",
        }}
      >
        <h1 className="font-serif text-xl" style={{ color: "#2C2C2C" }}>
          Admin <span className="text-gold">|</span> M & M
        </h1>
        <Button
          data-testid="admin-logout-button"
          variant="ghost"
          size="sm"
          onClick={logout}
          className="text-sm"
        >
          <LogOut size={16} className="mr-1" /> Esci
        </Button>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8">
        <Tabs defaultValue="dashboard">
          <TabsList className="mb-8">
            <TabsTrigger data-testid="tab-dashboard" value="dashboard">
              Dashboard
            </TabsTrigger>
            <TabsTrigger data-testid="tab-invitati" value="invitati">
              Invitati
            </TabsTrigger>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard">
            {dashboard ? (
              <div>
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
                  <StatCard
                    icon={Users}
                    label="Totale invitati"
                    value={dashboard.total_invitati}
                    color="#2C2C2C"
                  />
                  <StatCard
                    icon={UserCheck}
                    label="Confermati"
                    value={dashboard.confermati}
                    color="#8B9A6B"
                  />
                  <StatCard
                    icon={UserX}
                    label="Non partecipano"
                    value={dashboard.declinati}
                    color="#E74C3C"
                  />
                  <StatCard
                    icon={Clock}
                    label="In attesa"
                    value={dashboard.in_attesa}
                    color="#C5A572"
                  />
                  <StatCard
                    icon={Users}
                    label="Tot. partecipanti"
                    value={dashboard.totale_partecipanti}
                    color="#8B9A6B"
                  />
                </div>

                {/* Dietary stats */}
                {Object.keys(dashboard.intolleranze || {}).length > 0 && (
                  <div
                    className="bg-white rounded-sm p-6 mb-6"
                    style={{ border: "1px solid rgba(197,165,114,0.3)" }}
                  >
                    <h3 className="font-serif text-lg mb-3" style={{ color: "#2C2C2C" }}>
                      Intolleranze alimentari
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(dashboard.intolleranze).map(
                        ([name, count]) => (
                          <Badge
                            key={name}
                            variant="outline"
                            className="text-sm py-1 px-3"
                          >
                            {name}: {count}
                          </Badge>
                        )
                      )}
                    </div>
                  </div>
                )}

                {/* Group stats */}
                {Object.keys(dashboard.gruppi || {}).length > 0 && (
                  <div
                    className="bg-white rounded-sm p-6"
                    style={{ border: "1px solid rgba(197,165,114,0.3)" }}
                  >
                    <h3 className="font-serif text-lg mb-3" style={{ color: "#2C2C2C" }}>
                      Gruppi
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(dashboard.gruppi).map(([name, count]) => (
                        <Badge
                          key={name}
                          variant="outline"
                          className="text-sm py-1 px-3"
                        >
                          {name}: {count}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Download Excel */}
                <div className="mt-6">
                  <Button
                    data-testid="dashboard-download-excel"
                    className="rounded-sm"
                    style={{ backgroundColor: "#8B9A6B", color: "white" }}
                    onClick={downloadExcel}
                  >
                    <Download size={16} className="mr-2" /> Scarica elenco invitati (Excel)
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center py-10">
                <Loader2 size={32} className="animate-spin mx-auto text-sage" />
              </div>
            )}
          </TabsContent>

          {/* Guests Tab */}
          <TabsContent value="invitati">
            {/* Actions bar */}
            <div className="flex flex-col sm:flex-row gap-3 mb-6">
              <div className="relative flex-1">
                <Search
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2"
                  style={{ color: "#666" }}
                />
                <Input
                  data-testid="admin-search-input"
                  placeholder="Cerca invitato..."
                  value={searchQ}
                  onChange={(e) => setSearchQ(e.target.value)}
                  className="pl-9 h-10 rounded-sm"
                  style={{ border: "1px solid rgba(197,165,114,0.4)" }}
                />
              </div>
              <select
                data-testid="admin-status-filter"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="h-10 px-3 rounded-sm text-sm bg-white"
                style={{
                  border: "1px solid rgba(197,165,114,0.4)",
                  fontFamily: "'Manrope', sans-serif",
                }}
              >
                <option value="tutti">Tutti</option>
                <option value="confermato">Confermati</option>
                <option value="declinato">Non partecipano</option>
                <option value="in_attesa">In attesa</option>
              </select>
              <Button
                data-testid="admin-add-guest-button"
                className="rounded-sm"
                style={{ backgroundColor: "#8B9A6B", color: "white" }}
                onClick={openAdd}
              >
                <Plus size={16} className="mr-1" /> Aggiungi
              </Button>
              <Button
                data-testid="admin-csv-import-button"
                variant="outline"
                className="rounded-sm"
                onClick={() => setShowCSVDialog(true)}
              >
                <Upload size={16} className="mr-1" /> Importa CSV
              </Button>
              <Button
                data-testid="admin-download-excel-button"
                variant="outline"
                className="rounded-sm"
                style={{ borderColor: "#8B9A6B", color: "#8B9A6B" }}
                onClick={downloadExcel}
              >
                <Download size={16} className="mr-1" /> Scarica Excel
              </Button>
            </div>

            {/* Guest table grouped */}
            {loading ? (
              <div className="text-center py-10">
                <Loader2 size={32} className="animate-spin mx-auto text-sage" />
              </div>
            ) : (
              <div className="space-y-6">
                {Object.entries(groupedGuests).map(([group, gList]) => (
                  <div
                    key={group}
                    className="bg-white rounded-sm overflow-hidden"
                    style={{ border: "1px solid rgba(197,165,114,0.3)" }}
                  >
                    <div
                      className="px-4 py-2 text-sm font-medium"
                      style={{
                        backgroundColor: "#F5E6D3",
                        color: "#2C2C2C",
                      }}
                    >
                      {group} ({gList.length})
                    </div>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Nome</TableHead>
                          <TableHead className="hidden sm:table-cell">Max</TableHead>
                          <TableHead>Stato</TableHead>
                          <TableHead className="hidden md:table-cell">Partecipanti</TableHead>
                          <TableHead className="hidden lg:table-cell">Intolleranze</TableHead>
                          <TableHead className="text-right">Azioni</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {gList.map((g) => (
                          <TableRow key={g.id} data-testid={`guest-row-${g.id}`}>
                            <TableCell className="font-medium">
                              {g.nome} {g.cognome}
                              {g.note && (
                                <p className="text-xs mt-0.5" style={{ color: "#666" }}>
                                  {g.note}
                                </p>
                              )}
                            </TableCell>
                            <TableCell className="hidden sm:table-cell">
                              {g.max_accompagnatori}
                            </TableCell>
                            <TableCell>
                              <StatusBadge confermato={g.confermato} />
                            </TableCell>
                            <TableCell className="hidden md:table-cell">
                              {g.confermato ? g.numero_partecipanti : "-"}
                            </TableCell>
                            <TableCell className="hidden lg:table-cell">
                              {g.intolleranze?.length > 0
                                ? g.intolleranze.join(", ")
                                : "-"}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-1">
                                <Button
                                  data-testid={`edit-guest-${g.id}`}
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={() => openEdit(g)}
                                >
                                  <Pencil size={14} />
                                </Button>
                                {g.confermato !== null && (
                                  <Button
                                    data-testid={`reset-rsvp-${g.id}`}
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8"
                                    onClick={() => handleResetRSVP(g.id)}
                                    title="Reset RSVP"
                                  >
                                    <RotateCcw size={14} />
                                  </Button>
                                )}
                                <Button
                                  data-testid={`delete-guest-${g.id}`}
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-destructive"
                                  onClick={() => setDeleteGuest(g)}
                                >
                                  <Trash2 size={14} />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ))}
                {guests.length === 0 && (
                  <p className="text-center py-8" style={{ color: "#666" }}>
                    Nessun invitato trovato
                  </p>
                )}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog
        open={showAddDialog}
        onOpenChange={(open) => {
          if (!open) {
            setShowAddDialog(false);
            setEditGuest(null);
          }
        }}
      >
        <DialogContent data-testid="guest-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif">
              {editGuest ? "Modifica Invitato" : "Nuovo Invitato"}
            </DialogTitle>
            <DialogDescription>
              {editGuest
                ? "Modifica i dati dell'invitato"
                : "Inserisci i dati del nuovo invitato"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-sm mb-1 block">Nome *</Label>
              <Input
                data-testid="form-nome"
                value={formData.nome}
                onChange={(e) =>
                  setFormData({ ...formData, nome: e.target.value })
                }
                className="rounded-sm"
              />
            </div>
            <div>
              <Label className="text-sm mb-1 block">Cognome *</Label>
              <Input
                data-testid="form-cognome"
                value={formData.cognome}
                onChange={(e) =>
                  setFormData({ ...formData, cognome: e.target.value })
                }
                className="rounded-sm"
              />
            </div>
            <div>
              <Label className="text-sm mb-1 block">Gruppo / Famiglia</Label>
              <Input
                data-testid="form-gruppo"
                value={formData.gruppo}
                onChange={(e) =>
                  setFormData({ ...formData, gruppo: e.target.value })
                }
                className="rounded-sm"
                placeholder="Es. Famiglia Rossi"
              />
            </div>
            <div>
              <Label className="text-sm mb-1 block">Max accompagnatori</Label>
              <Input
                data-testid="form-max-accompagnatori"
                type="number"
                min={1}
                max={10}
                value={formData.max_accompagnatori}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    max_accompagnatori: parseInt(e.target.value) || 1,
                  })
                }
                className="rounded-sm w-24"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              className="rounded-sm"
              onClick={() => {
                setShowAddDialog(false);
                setEditGuest(null);
              }}
            >
              Annulla
            </Button>
            <Button
              data-testid="form-save-button"
              className="rounded-sm"
              style={{ backgroundColor: "#8B9A6B", color: "white" }}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? <Loader2 className="animate-spin mr-1" size={16} /> : null}
              {editGuest ? "Salva" : "Aggiungi"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog
        open={!!deleteGuest}
        onOpenChange={(open) => !open && setDeleteGuest(null)}
      >
        <DialogContent data-testid="delete-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif">Conferma eliminazione</DialogTitle>
            <DialogDescription>
              Sei sicuro di voler eliminare{" "}
              <strong>
                {deleteGuest?.nome} {deleteGuest?.cognome}
              </strong>
              ? Questa azione non può essere annullata.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              className="rounded-sm"
              onClick={() => setDeleteGuest(null)}
            >
              Annulla
            </Button>
            <Button
              data-testid="confirm-delete-button"
              variant="destructive"
              className="rounded-sm"
              onClick={handleDelete}
            >
              Elimina
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CSV Import Dialog */}
      <Dialog
        open={showCSVDialog}
        onOpenChange={(open) => !open && setShowCSVDialog(false)}
      >
        <DialogContent data-testid="csv-import-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif">Importa Invitati da CSV</DialogTitle>
            <DialogDescription>
              Il file CSV deve avere le colonne: nome, cognome, gruppo, max_accompagnatori
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Button
              data-testid="download-csv-template"
              variant="outline"
              className="rounded-sm w-full"
              onClick={downloadTemplate}
            >
              <Download size={16} className="mr-2" /> Scarica Template CSV
            </Button>
            <Separator />
            <Label
              htmlFor="csv-file"
              className="block text-center cursor-pointer p-6 rounded-sm border-2 border-dashed"
              style={{ borderColor: "rgba(197,165,114,0.4)" }}
            >
              <Upload size={24} className="mx-auto mb-2 text-gold" />
              <span className="text-sm" style={{ color: "#666" }}>
                Clicca per selezionare il file CSV
              </span>
            </Label>
            <input
              id="csv-file"
              data-testid="csv-file-input"
              type="file"
              accept=".csv"
              className="hidden"
              onChange={handleCSVUpload}
            />
          </div>
        </DialogContent>
      </Dialog>
    </main>
  );
}
